<template>
  <!-- A project from the new builder (S5_Project): cover, title and intro,
       the facts table, the story in blocks (before and after pairs, photos),
       the customer's review, "Get a quote like this" and previous / next. -->
  <article class="s2-proj t-band" data-variant="1">
    <header class="s2-proj__cover">
      <Media v-if="project.cover" :media="project.cover" ratio="21 / 9" alt="" class="s2-proj__coverimg" />
      <div class="t-band__inner s2-proj__headinner">
        <p v-if="project.typeLabel" class="t-eyebrow">{{ project.typeLabel }}</p>
        <h1 class="t-h1">{{ project.title }}</h1>
        <p v-if="project.intro" class="t-lead">{{ project.intro }}</p>
      </div>
    </header>
    <div class="t-band__inner s2-proj__body">
      <div class="s2-proj__main">
        <BlockRenderer :blocks="project.blocks" />
        <figure v-if="project.review" class="s2-proj__review t-card mo-reveal">
          <span class="s2-stars" :aria-label="`${project.review.rating} out of 5`">{{ '★'.repeat(project.review.rating) }}</span>
          <blockquote>“{{ project.review.body }}”</blockquote>
          <figcaption class="t-muted">{{ project.review.name }}<template v-if="project.review.town">, {{ project.review.town }}</template></figcaption>
        </figure>
      </div>
      <aside class="s2-proj__side">
        <dl v-if="project.facts.length" class="s2-proj__facts t-card">
          <template v-for="f in project.facts" :key="f.label"><dt class="t-muted">{{ f.label }}</dt><dd>{{ f.value }}</dd></template>
        </dl>
        <div class="s2-proj__cta t-card">
          <strong>Want something like this?</strong>
          <a :href="back(project.quoteLink)" class="t-btn">Get a quote like this</a>
        </div>
      </aside>
    </div>
    <nav v-if="prev || next" class="t-band__inner s2-proj__pn" aria-label="More projects">
      <a v-if="prev" :href="back(prev.path)" class="s2-proj__pnlink"><span class="t-eyebrow">← Previous project</span><strong>{{ prev.title }}</strong></a>
      <span v-else />
      <a v-if="next" :href="back(next.path)" class="s2-proj__pnlink s2-proj__pnlink--next"><span class="t-eyebrow">Next project →</span><strong>{{ next.title }}</strong></a>
    </nav>
  </article>
</template>

<script setup>
import {computed} from "vue";
import Media from "../shared/Media.vue";
import BlockRenderer from "../blocks/BlockRenderer.vue";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}});
const project = computed(() => props.section.project || {blocks: [], facts: []});
const prev = computed(() => props.section.prev);
const next = computed(() => props.section.next);
</script>

<style scoped>
.s2-proj__cover { position: relative; }
.s2-proj__coverimg :deep(img) { border-radius: 0; max-height: 72vh; }
.s2-proj__headinner { padding-bottom: 0; }
.s2-proj__headinner .t-h1 { margin: .4rem 0 0; }
.s2-proj__body { display: grid; grid-template-columns: minmax(0, 1fr) 18rem; gap: clamp(1.5rem, 4vw, 3rem); align-items: start; }
.s2-proj__side { position: sticky; top: 6rem; display: grid; gap: 1rem; }
.s2-proj__facts { display: grid; grid-template-columns: auto 1fr; gap: .45rem 1rem; margin: 0; padding: 1rem 1.2rem; }
.s2-proj__facts dd { margin: 0; font-weight: 600; }
.s2-proj__cta { display: flex; flex-direction: column; gap: .8rem; padding: 1rem 1.2rem; }
.s2-proj__review { margin: 2rem 0 0; padding: 1.25rem 1.5rem; }
.s2-proj__review blockquote { margin: .5rem 0; font-size: 1.15rem; line-height: 1.45; font-family: var(--t-font-display); }
.s2-stars { color: var(--t-accent); letter-spacing: .1em; }
.s2-proj__pn { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; border-top: 1px solid var(--t-line); }
.s2-proj__pnlink { display: flex; flex-direction: column; gap: .25rem; color: inherit; text-decoration: none; }
.s2-proj__pnlink--next { text-align: right; }
@media (max-width: 860px) {
  .s2-proj__body { grid-template-columns: 1fr; }
  .s2-proj__side { position: static; order: -1; }
  .s2-proj__pn { grid-template-columns: 1fr; } .s2-proj__pnlink--next { text-align: left; }
}
</style>
